import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { intelligentReportingAPI } from '../../services/intelligentReportingAPI';

// Async thunks
export const generateReport = createAsyncThunk(
  'aiReports/generate',
  async ({ query, outputFormat }, { rejectWithValue }) => {
    try {
      const response = await intelligentReportingAPI.generateReport(query, outputFormat);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchReportHTML = createAsyncThunk(
  'aiReports/fetchHTML',
  async (reportId, { rejectWithValue }) => {
    try {
      const response = await intelligentReportingAPI.getReportHTML(reportId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const downloadReportPDF = createAsyncThunk(
  'aiReports/downloadPDF',
  async (reportId, { rejectWithValue }) => {
    try {
      const blob = await intelligentReportingAPI.downloadPDF(reportId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${reportId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, reportId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const interpretQuery = createAsyncThunk(
  'aiReports/interpret',
  async (query, { rejectWithValue }) => {
    try {
      const response = await intelligentReportingAPI.testIntent(query);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Slice
const aiReportsSlice = createSlice({
  name: 'aiReports',
  initialState: {
    // Query state
    currentQuery: '',
    queryInterpretation: null,

    // Report generation state
    reportId: null,
    reportStatus: 'idle', // 'idle' | 'generating' | 'completed' | 'failed'
    reportData: null,

    // HTML fetch state
    htmlContent: null,
    htmlLoading: false,

    // Polling state (kept for compatibility, but no longer used)
    isPolling: false,
    pollingAttempts: 0,

    // UI state
    error: null,
    isDownloading: false,
  },
  reducers: {
    setQuery: (state, action) => {
      state.currentQuery = action.payload;
    },
    clearReport: (state) => {
      state.reportId = null;
      state.reportStatus = 'idle';
      state.reportData = null;
      state.htmlContent = null;
      state.htmlLoading = false;
      state.error = null;
      state.pollingAttempts = 0;
    },
    startPolling: (state) => {
      state.isPolling = true;
    },
    stopPolling: (state) => {
      state.isPolling = false;
      state.pollingAttempts = 0;
    },
    incrementPollingAttempts: (state) => {
      state.pollingAttempts += 1;
    },
    clearQueryInterpretation: (state) => {
      state.queryInterpretation = null;
    },
  },
  extraReducers: (builder) => {
    // Generate report (synchronous — returns completed report directly)
    builder
      .addCase(generateReport.pending, (state) => {
        state.reportStatus = 'generating';
        state.error = null;
        state.htmlContent = null;
      })
      .addCase(generateReport.fulfilled, (state, action) => {
        state.reportId = action.payload.report_id;
        state.reportStatus = 'completed';
        state.isPolling = false;
        state.reportData = {
          metadata: action.payload.metadata,
          pdfUrl: action.payload.pdf_url,
          htmlUrl: action.payload.html_url,
        };
      })
      .addCase(generateReport.rejected, (state, action) => {
        state.reportStatus = 'failed';
        state.error = action.payload?.message || action.payload?.detail || 'Failed to generate report';
      });

    // Fetch report HTML content
    builder
      .addCase(fetchReportHTML.pending, (state) => {
        state.htmlLoading = true;
      })
      .addCase(fetchReportHTML.fulfilled, (state, action) => {
        state.htmlLoading = false;
        // Response may be a string (raw HTML) or an object with html_content
        state.htmlContent = typeof action.payload === 'string'
          ? action.payload
          : action.payload.html_content || action.payload;
      })
      .addCase(fetchReportHTML.rejected, (state, action) => {
        state.htmlLoading = false;
        state.error = action.payload?.message || 'Failed to fetch report HTML';
      });

    // Download PDF
    builder
      .addCase(downloadReportPDF.pending, (state) => {
        state.isDownloading = true;
      })
      .addCase(downloadReportPDF.fulfilled, (state) => {
        state.isDownloading = false;
      })
      .addCase(downloadReportPDF.rejected, (state, action) => {
        state.isDownloading = false;
        state.error = action.payload?.message || 'Failed to download PDF';
      });

    // Interpret query
    builder
      .addCase(interpretQuery.fulfilled, (state, action) => {
        state.queryInterpretation = action.payload;
      })
      .addCase(interpretQuery.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to interpret query';
      });
  },
});

export const {
  setQuery,
  clearReport,
  startPolling,
  stopPolling,
  incrementPollingAttempts,
  clearQueryInterpretation,
} = aiReportsSlice.actions;

export default aiReportsSlice.reducer;
